import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, MessageCircle, Clock, FileText } from "lucide-react";
import { koperasiInfo } from "@/lib/dummyData";

// Halaman kebijakan & kontak publik — wajib ada untuk verifikasi payment gateway (Midtrans).
// Data kontak/legal diambil dari koperasiInfo (dikonfigurasi lewat REACT_APP_COMPANY_* di frontend/.env).

const UPDATED = "25 September 2026";
const waLink = (n) => `https://wa.me/${String(n).replace(/\D/g, "").replace(/^0/, "62")}`;

function LegalPage({ tag, title, children }) {
  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-10 lg:py-16">
      <div className="pill-tag bg-forest/10 text-forest mb-3">{tag}</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">{title}</h1>
      <p className="text-xs text-ink/55 mt-2">Terakhir diperbarui: {UPDATED}</p>
      <div className="legal-body mt-8 space-y-6 text-sm text-ink/80 leading-relaxed [&_h2]:font-display [&_h2]:font-semibold [&_h2]:text-lg [&_h2]:text-ink [&_h2]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_a]:text-forest [&_a]:font-semibold">
        {children}
      </div>
      <div className="mt-12 rounded-2xl bg-white border border-stone-soft p-5 text-sm text-ink/75">
        Pertanyaan tentang kebijakan ini? Hubungi kami di <a className="text-forest font-semibold" href={`mailto:${koperasiInfo.email}`}>{koperasiInfo.email}</a>{" "}
        atau lihat halaman <Link to="/kontak" className="text-forest font-semibold">Kontak</Link>.
      </div>
    </div>
  );
}

export function Terms() {
  const k = koperasiInfo;
  return (
    <LegalPage tag="Legal" title="Syarat & Ketentuan">
      <p>
        Syarat & Ketentuan ini mengatur penggunaan situs <b>{k.website}</b> dan layanan belanja yang dikelola oleh{" "}
        <b>{k.name}</b> (Badan Hukum No. {k.legalNumber}) ("Koperasi"). Dengan mendaftar, masuk, atau melakukan
        pembelian, Anda menyetujui ketentuan berikut.
      </p>
      <section>
        <h2>1. Akun & Keanggotaan</h2>
        <ol>
          <li>Pembeli wajib memiliki akun dan masuk (login) sebelum membuat pesanan.</li>
          <li>Anggota koperasi yang telah diverifikasi memperoleh <b>harga anggota</b>; pengguna lain membayar harga publik.</li>
          <li>Anda bertanggung jawab menjaga kerahasiaan kata sandi dan seluruh aktivitas pada akun Anda.</li>
          <li>Data yang Anda berikan harus benar. Koperasi berhak menangguhkan akun yang terindikasi penyalahgunaan atau penipuan.</li>
        </ol>
      </section>
      <section>
        <h2>2. Produk & Harga</h2>
        <ol>
          <li>Produk dijual oleh Koperasi dan oleh anggota koperasi yang terdaftar sebagai penjual (produk anggota).</li>
          <li>Harga ditampilkan dalam Rupiah dan <b>sudah termasuk fee layanan koperasi 5%</b>. Ongkos kirim ditampilkan terpisah saat checkout.</li>
          <li>Harga akhir dihitung ulang oleh sistem saat checkout sesuai status keanggotaan Anda. Harga yang tercantum pada ringkasan pesanan adalah harga yang berlaku.</li>
          <li>Ketersediaan stok dapat berubah sewaktu-waktu. Bila stok tidak mencukupi setelah pembayaran, dana dikembalikan sesuai <Link to="/kebijakan-pengembalian">Kebijakan Pengembalian Dana</Link>.</li>
        </ol>
      </section>
      <section>
        <h2>3. Pemesanan & Pembayaran</h2>
        <ol>
          <li>Pesanan dibuat melalui halaman Keranjang → Checkout. Setiap pesanan memiliki nomor unik (format <code>ORD-…</code>).</li>
          <li>Metode pembayaran: <b>pembayaran online melalui Midtrans</b> (Virtual Account bank, QRIS, e-wallet, kartu kredit/debit), transfer manual ke rekening resmi Koperasi, atau saldo Dompet Anggota.</li>
          <li>Pembayaran online diproses oleh PT Midtrans sebagai payment gateway berizin Bank Indonesia. Koperasi tidak menyimpan data kartu Anda.</li>
          <li>Pesanan dengan pembayaran online yang tidak dibayar dalam <b>24 jam</b> dibatalkan otomatis dan stok dikembalikan.</li>
          <li>Dana pembayaran ditahan di rekening bersama Koperasi (escrow) dan baru diteruskan ke penjual setelah Anda mengonfirmasi barang diterima.</li>
        </ol>
      </section>
      <section>
        <h2>4. Pengiriman</h2>
        <ol>
          <li>Pilihan pengiriman: Kurir Toko (estimasi 2–3 hari kerja), Kurir Toko Express, atau Ambil di Gerai (gratis).</li>
          <li>Pesanan diproses setelah pembayaran terverifikasi. Nomor resi (bila ada) dapat dilihat di menu Pesanan Saya.</li>
          <li>Pastikan alamat dan nomor telepon penerima benar. Kegagalan pengiriman karena data yang salah menjadi tanggung jawab pembeli.</li>
        </ol>
      </section>
      <section>
        <h2>5. Pembatalan, Komplain & Pengembalian</h2>
        <p>Diatur dalam <Link to="/kebijakan-pengembalian">Kebijakan Pengembalian Dana</Link> yang merupakan bagian tidak terpisahkan dari Syarat & Ketentuan ini.</p>
      </section>
      <section>
        <h2>6. Larangan</h2>
        <ul>
          <li>Menggunakan layanan untuk transaksi fiktif, pencucian uang, atau aktivitas melanggar hukum.</li>
          <li>Menjual barang terlarang, palsu, atau melanggar hak kekayaan intelektual pihak lain.</li>
          <li>Mengganggu, meretas, atau membebani sistem Koperasi.</li>
        </ul>
      </section>
      <section>
        <h2>7. Batasan Tanggung Jawab</h2>
        <p>
          Koperasi berupaya menjaga layanan tetap tersedia dan akurat, namun tidak bertanggung jawab atas gangguan yang
          disebabkan oleh pihak ketiga (bank, payment gateway, kurir) atau keadaan kahar. Tanggung jawab Koperasi atas
          suatu pesanan terbatas pada nilai pesanan tersebut.
        </p>
      </section>
      <section>
        <h2>8. Hukum yang Berlaku</h2>
        <p>
          Ketentuan ini tunduk pada hukum Republik Indonesia. Perselisihan diselesaikan secara musyawarah; bila tidak
          tercapai, melalui lembaga penyelesaian sengketa konsumen atau pengadilan yang berwenang.
        </p>
      </section>
      <section>
        <h2>9. Perubahan Ketentuan</h2>
        <p>Koperasi dapat memperbarui ketentuan ini sewaktu-waktu. Versi terbaru selalu tersedia di halaman ini.</p>
      </section>
    </LegalPage>
  );
}

export function RefundPolicy() {
  const k = koperasiInfo;
  return (
    <LegalPage tag="Legal" title="Kebijakan Pengembalian Dana">
      <p>
        Kebijakan ini menjelaskan kapan dan bagaimana dana pembelian di <b>{k.name}</b> dikembalikan. Karena dana
        pembayaran ditahan di rekening bersama koperasi (escrow) sampai pesanan selesai, pengembalian dapat diproses
        dengan aman.
      </p>
      <section>
        <h2>1. Pembatalan Sebelum Pengiriman</h2>
        <ul>
          <li>Pesanan pembayaran online yang <b>belum dibayar</b> otomatis batal setelah 24 jam tanpa biaya apa pun. Pesanan lain yang belum dibayar dapat dibatalkan melalui layanan pelanggan.</li>
          <li>Pesanan yang <b>sudah dibayar tetapi belum dikirim</b> dapat dibatalkan dengan menghubungi layanan pelanggan. Dana dikembalikan 100%.</li>
          <li>Bila penjual tidak dapat memenuhi pesanan (stok habis), pesanan dibatalkan dan dana dikembalikan 100%.</li>
        </ul>
      </section>
      <section>
        <h2>2. Komplain Setelah Barang Diterima</h2>
        <p>Komplain dapat diajukan paling lambat <b>3 (tiga) hari</b> setelah barang diterima dan <b>sebelum</b> Anda menekan tombol "Terima Barang", untuk kondisi berikut:</p>
        <ul>
          <li>Barang tidak sesuai pesanan (jenis, ukuran, jumlah).</li>
          <li>Barang rusak, cacat, atau kedaluwarsa saat diterima.</li>
          <li>Barang tidak diterima padahal status menunjukkan telah dikirim.</li>
        </ul>
        <p>Sertakan nomor pesanan, foto/video barang saat dibuka (unboxing), dan penjelasan singkat.</p>
      </section>
      <section>
        <h2>3. Barang yang Tidak Dapat Dikembalikan</h2>
        <ul>
          <li>Produk segar/mudah rusak yang telah diterima dalam kondisi baik lalu rusak karena penyimpanan pembeli.</li>
          <li>Produk yang telah dibuka/dipakai, kecuali cacat produksi.</li>
          <li>Komplain yang diajukan setelah pesanan dikonfirmasi selesai ("Terima Barang") atau lewat dari 3 hari.</li>
        </ul>
      </section>
      <section>
        <h2>4. Proses & Waktu Pengembalian Dana</h2>
        <ol>
          <li>Komplain ditinjau maksimal <b>2 hari kerja</b>. Bila perlu, barang diminta dikirim kembali (ongkos kirim retur ditanggung Koperasi/penjual untuk kesalahan dari pihak kami).</li>
          <li>Setelah disetujui, dana dikembalikan dalam <b>maksimal 7 hari kerja</b> ke:
            <ul>
              <li>metode pembayaran asal untuk pembayaran online via Midtrans (sesuai ketentuan bank/penerbit), atau</li>
              <li>rekening bank atas nama pembeli untuk transfer manual, atau</li>
              <li>saldo Dompet Anggota untuk pembayaran menggunakan dompet.</li>
            </ul>
          </li>
          <li>Pengembalian dapat berupa dana penuh, sebagian (untuk sebagian barang), atau penggantian barang sesuai kesepakatan.</li>
        </ol>
      </section>
      <section>
        <h2>5. Cara Mengajukan</h2>
        <p>
          Hubungi layanan pelanggan melalui WhatsApp <a href={waLink(k.wa)} target="_blank" rel="noreferrer">{k.wa}</a> atau email{" "}
          <a href={`mailto:${k.email}`}>{k.email}</a> ({k.hours}).
        </p>
      </section>
    </LegalPage>
  );
}

export function PrivacyPolicy() {
  const k = koperasiInfo;
  return (
    <LegalPage tag="Legal" title="Kebijakan Privasi">
      <p>
        {k.name} menghormati privasi Anda dan memproses data pribadi sesuai Undang-Undang No. 27 Tahun 2022 tentang
        Pelindungan Data Pribadi (UU PDP).
      </p>
      <section>
        <h2>1. Data yang Kami Kumpulkan</h2>
        <ul>
          <li>Data identitas: nama, NIK, tempat lahir, dokumen KTP/pasfoto (khusus pendaftaran anggota).</li>
          <li>Data kontak: email, nomor telepon, alamat & kode pos.</li>
          <li>Data transaksi: pesanan, pembayaran, simpanan, dan riwayat dompet.</li>
        </ul>
      </section>
      <section>
        <h2>2. Penggunaan Data</h2>
        <ul>
          <li>Memverifikasi keanggotaan dan menentukan harga anggota.</li>
          <li>Memproses pesanan, pembayaran, pengiriman, dan pengembalian dana.</li>
          <li>Menghitung simpanan dan Sisa Hasil Usaha (SHU) anggota.</li>
          <li>Memenuhi kewajiban hukum dan pelaporan koperasi.</li>
        </ul>
      </section>
      <section>
        <h2>3. Keamanan</h2>
        <p>
          NIK disimpan terenkripsi (AES-256) dan ditampilkan tersamar. Kata sandi disimpan dalam bentuk hash. Data
          pembayaran kartu diproses langsung oleh Midtrans dan tidak disimpan oleh Koperasi.
        </p>
      </section>
      <section>
        <h2>4. Berbagi Data dengan Pihak Ketiga</h2>
        <p>Data hanya dibagikan seperlunya kepada payment gateway (Midtrans), kurir pengiriman, dan instansi berwenang sesuai hukum. Kami tidak menjual data pribadi Anda.</p>
      </section>
      <section>
        <h2>5. Hak Anda</h2>
        <p>Anda berhak mengakses, memperbaiki, dan meminta penghapusan data pribadi Anda dengan menghubungi <a href={`mailto:${k.email}`}>{k.email}</a>.</p>
      </section>
    </LegalPage>
  );
}

export function Contact() {
  const k = koperasiInfo;
  const items = [
    { icon: MapPin, label: "Alamat Kantor", value: k.address },
    { icon: Phone, label: "Telepon", value: k.phone, href: `tel:${String(k.phone).replace(/[^\d+]/g, "")}` },
    { icon: MessageCircle, label: "WhatsApp Layanan Pelanggan", value: k.wa, href: waLink(k.wa) },
    { icon: Mail, label: "Email", value: k.email, href: `mailto:${k.email}` },
    { icon: Clock, label: "Jam Operasional", value: k.hours },
    { icon: FileText, label: "Badan Hukum", value: k.legalNumber },
  ];
  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-10 lg:py-16">
      <div className="pill-tag bg-forest/10 text-forest mb-3">Kontak</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Hubungi Kami</h1>
      <p className="text-ink/75 mt-3 text-sm">
        Layanan pelanggan {k.name} siap membantu pertanyaan seputar keanggotaan, pesanan, pembayaran, dan pengembalian dana.
      </p>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        {items.map((it) => (
          <div key={it.label} className="rounded-2xl bg-white border border-stone-soft p-5 flex gap-3">
            <it.icon size={18} className="text-forest shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-ink/55">{it.label}</div>
              {it.href ? (
                <a href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-sm font-semibold text-ink hover:text-forest break-words">{it.value}</a>
              ) : (
                <div className="text-sm font-semibold text-ink break-words">{it.value}</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-sm text-ink/75">
        Lihat juga: <Link to="/syarat-ketentuan" className="text-forest font-semibold">Syarat & Ketentuan</Link> ·{" "}
        <Link to="/kebijakan-pengembalian" className="text-forest font-semibold">Kebijakan Pengembalian Dana</Link> ·{" "}
        <Link to="/kebijakan-privasi" className="text-forest font-semibold">Kebijakan Privasi</Link>
      </div>
    </div>
  );
}
